import { hoursToMilliseconds, minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';

import { RuntimeEnvironmentOptions, getEnvFromHostname } from '@growtherapy/analytics';

import { Auth0Config } from './auth/types';
import { logger } from './datadog/logger';
import { Statsig as BaseStatsig } from './statsig/StatsigProvider';
import { dynamicConfig } from './statsig/gates';

export const isDevMode = import.meta.env.DEV;
export const IS_DEV_APP_SETTINGS_ENABLED_KEY = 'is-dev-app-settings-enabled';
export const DEV_APP_SETTINGS_KEY = 'dev-app-settings';
export const PROVIDER_PATH = '/provider';
export const GRAPHQL_PATH = '/api/graphql';

/**
 * Only used on the server
 */
export interface ServerSideTelehealthConfig {
  openai_base_url?: string;
  event_consumer_queue?: 'high' | 'low' | 'normal' | 'io';
}

/**
 * this type should line up with the JSON schema for telehealth_config in Statsig dashboard - see telehealth_config.schema.json
 * so be sure to keep that up-to-date with TelehealthConfig.
 */
export interface TelehealthConfig extends ServerSideTelehealthConfig {
  max_twilio_websocket_connection_retries_before_fallback: number;
  session_interaction_prompt_config: SessionInteractionPromptConfig;
  unaccompanied_participant_interval: number;
  audio_recording_upload_interval_seconds: number;
  screen_share_config: DisplayMediaStreamOptions;
  max_data_track_message_size_bytes: number;
  zoom_failover_config: ZoomFailoverConfig;
  whiteboard_eventual_consistency_interval_seconds: number;
  minimum_recording_seconds?: number;
  prescriber_minimum_recording_seconds?: number;
  short_session_percentage_threshold?: number;
  auth0_config?: Pick<Auth0Config, 'authorizeTimeoutInSeconds' | 'httpTimeoutInSeconds'>;
}

export interface SessionInteractionPromptConfig {
  message: string;
  'TREE-349.message': string;
  showAfterMs: number;
}

export interface DisplayMediaStreamOptions {
  video: {
    frameRate?: { max: number };
    width?: { max: number };
    height?: { max: number };
  };
  audio: boolean;
}

export interface ZoomFailoverConfig {
  joinRetries: number;
  joinRetryDelayMs: number;
}

export const DEFAULTS: TelehealthConfig = {
  max_twilio_websocket_connection_retries_before_fallback: 0,
  session_interaction_prompt_config: {
    'TREE-349.message':
      'Audio and video have been disabled by your browser. Please click the {{mic-off-icon}} and {{cam-off-icon}} buttons below to enable.',
    message: 'Your session is about to start — click or tap the screen to continue.',
    showAfterMs: secondsToMilliseconds(3),
  },
  unaccompanied_participant_interval: 15,
  audio_recording_upload_interval_seconds: 30,
  screen_share_config: {
    video: {
      frameRate: { max: 15 },
      width: { max: 1280 },
      height: { max: 720 },
    },
    audio: true,
  },
  max_data_track_message_size_bytes: 0,
  zoom_failover_config: {
    joinRetries: 5,
    joinRetryDelayMs: secondsToMilliseconds(1),
  },
  /**
   * This interval shouldn't be too short as the whiteboard snapshot can be large.
   */
  whiteboard_eventual_consistency_interval_seconds: 60,
  auth0_config: {},
};

export function getTelehealthConfig(options?: { Statsig?: typeof BaseStatsig }): TelehealthConfig {
  // returns the dynamic config for the telehealth_config
  // https://console.statsig.com/ehmOdP8c3qeFibZ6BvwUV/dynamic_configs/telehealth_config
  // with each key defaulted, since there is no guarantee that the JSON in Statsig
  // will contain all the required keys.
  // It _should_ follow the scheme defined in client/src/statsig/telehealth_config.schema.json
  // so be sure to keep that up-to-date with TelehealthConfig.
  const { Statsig = BaseStatsig } = options ?? {};
  const config = Statsig.getDynamicConfig(dynamicConfig);

  return {
    max_twilio_websocket_connection_retries_before_fallback:
      config?.get(
        'max_twilio_websocket_connection_retries_before_fallback',
        DEFAULTS.max_twilio_websocket_connection_retries_before_fallback,
      ) ?? DEFAULTS.max_twilio_websocket_connection_retries_before_fallback,
    session_interaction_prompt_config: {
      ...DEFAULTS.session_interaction_prompt_config,
      ...config?.get('session_interaction_prompt_config', DEFAULTS.session_interaction_prompt_config),
    },
    unaccompanied_participant_interval:
      config?.get('unaccompanied_participant_interval', DEFAULTS.unaccompanied_participant_interval) ??
      DEFAULTS.unaccompanied_participant_interval,
    audio_recording_upload_interval_seconds:
      config?.get('audio_recording_upload_interval_seconds', DEFAULTS.audio_recording_upload_interval_seconds) ??
      DEFAULTS.audio_recording_upload_interval_seconds,
    screen_share_config: {
      ...DEFAULTS.screen_share_config,
      ...config?.get('screen_share_config', DEFAULTS.screen_share_config),
    },
    max_data_track_message_size_bytes: (() => {
      const val =
        config?.get('max_data_track_message_size_bytes', DEFAULTS.max_data_track_message_size_bytes) ??
        DEFAULTS.max_data_track_message_size_bytes;
      return val >= 0 ? val : 0;
    })(),
    zoom_failover_config: {
      ...DEFAULTS.zoom_failover_config,
      ...config?.get('zoom_failover_config', DEFAULTS.zoom_failover_config),
    },
    whiteboard_eventual_consistency_interval_seconds:
      config?.get<number>(
        'whiteboard_eventual_consistency_interval_seconds',
        DEFAULTS.whiteboard_eventual_consistency_interval_seconds,
      ) ?? DEFAULTS.whiteboard_eventual_consistency_interval_seconds,
    auth0_config:
      config?.get<TelehealthConfig['auth0_config']>('auth0_config', DEFAULTS.auth0_config) ?? DEFAULTS.auth0_config,
  };
}

export function getSessionInteractionInfo(
  ...args: Parameters<typeof getTelehealthConfig>
): SessionInteractionPromptConfig {
  return getTelehealthConfig(...args).session_interaction_prompt_config;
}

export function getZoomFailoverConfig(...args: Parameters<typeof getTelehealthConfig>): ZoomFailoverConfig {
  return getTelehealthConfig(...args).zoom_failover_config;
}

/**
 * Returns the maximum size of a data track message in bytes. Defaults to 0
 * (falsey) i.e. no max size.
 *
 * This is necessary because large messages can crash data track functionality.
 * https://www.twilio.com/docs/video/using-datatrack-api#overview
 */
export function getMaxDataChannelMessageSizeBytes(...args: Parameters<typeof getTelehealthConfig>) {
  return getTelehealthConfig(...args).max_data_track_message_size_bytes;
}

export function getVisitorPresenceUpdateIntervalMs(...args: Parameters<typeof getTelehealthConfig>) {
  return secondsToMilliseconds(getTelehealthConfig(...args).unaccompanied_participant_interval);
}

export function getAudioRecordingUploadIntervalMs(...args: Parameters<typeof getTelehealthConfig>) {
  return secondsToMilliseconds(getTelehealthConfig(...args).audio_recording_upload_interval_seconds);
}

export const NOTIFICATION_VOLUME = 0.33;
// Should correspond with VISITOR_ACCESS_DOC_TTL_SECONDS on the backend
export const VISITOR_ACCESS_DOC_TTL_MS = hoursToMilliseconds(1);
export const STAGING_MONOLITH_GQL_PATH = '/staging/monolith/graphql';
export const DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS = minutesToMilliseconds(1);

export interface AppConfig {
  marketplaceOrigin: string;
  providerOrigin: string;
  version: string;
  monolithGqlPath: string;
  providerPath: string;
  graphqlPath: string;
  monolithBackendOrigin: string;
  videoPlatformOverride: string;
}

const BASE_CONFIG = {
  version: import.meta.env.VITE_VERSION as string,
  providerPath: PROVIDER_PATH,
  graphqlPath: GRAPHQL_PATH,
};

export function getDevAppSettings() {
  const isProd = getEnvFromHostname({ hostname: window.location.hostname }) === RuntimeEnvironmentOptions.production;
  // staging allowed for QA purposes
  if (isProd) {
    return {};
  }
  try {
    const isDevConnectionSettingsEnabled = !!parseInt(localStorage.getItem(IS_DEV_APP_SETTINGS_ENABLED_KEY) ?? '0', 10);
    if (!isDevConnectionSettingsEnabled) return {};
    const settings = JSON.parse(localStorage.getItem(DEV_APP_SETTINGS_KEY) || '{}');
    logger.debug('Using dev app settings', settings);
    return settings;
  } catch (error) {
    logger.debug('Error parsing dev app settings', {}, error as Error);
    return {};
  }
}

export function getAppConfig(): AppConfig {
  const env = getEnvFromHostname({ hostname: window.location.hostname });
  const devConfig = getDevAppSettings();
  switch (env) {
    case RuntimeEnvironmentOptions.production:
      return {
        ...BASE_CONFIG,
        marketplaceOrigin: 'https://growtherapy.com',
        providerOrigin: 'https://provider.growtherapy.com',
        monolithBackendOrigin: 'https://provider.growtherapy.com',
        monolithGqlPath: '/graphql',
        ...devConfig,
      };
    case RuntimeEnvironmentOptions.development:
      return {
        ...BASE_CONFIG,
        marketplaceOrigin: `https://${import.meta.env.VITE_DEVELOPMENT_MONOLITH_BASE_URL}`,
        providerOrigin: `https://provider.${import.meta.env.VITE_DEVELOPMENT_MONOLITH_BASE_URL}`,
        monolithBackendOrigin: `https://provider.${import.meta.env.VITE_DEVELOPMENT_MONOLITH_BASE_URL}`,
        monolithGqlPath: '/graphql',
        ...devConfig,
      };
    case RuntimeEnvironmentOptions.staging:
      return {
        ...BASE_CONFIG,
        marketplaceOrigin: 'https://growtherapystaging.com',
        providerOrigin: 'https://provider.growtherapystaging.com',
        monolithBackendOrigin: 'https://provider.growtherapystaging.com',
        monolithGqlPath: '/graphql',
        ...devConfig,
      };
    default:
      return {
        ...BASE_CONFIG,
        marketplaceOrigin: 'https://growtherapylocal.com:3000',
        providerOrigin: 'https://provider.growtherapylocal.com:3000',
        monolithBackendOrigin: 'https://telehealth.growtherapylocal.com',
        monolithGqlPath: '/monolith/graphql',
        version: 'local',
        ...devConfig,
      };
  }
}
